import * as echarts from 'echarts/core';
import {ChordChart} from 'echarts/charts';
import {AriaComponent, TitleComponent, TooltipComponent} from 'echarts/components';
import {CanvasRenderer, SVGRenderer} from 'echarts/renderers';
import type {EChartsOption} from 'echarts';
import {appendItemCountsToAriaLabel, ECHARTS_THEME} from '../../../helper/echarts-theme';
import {
  NetworkLevelFlow
} from "@app/netscope/model/data-models";
import {
  FlowsRendererInterface,
  NetscopeCommonFlowviewComponent
} from "@app/netscope/netscope-common-flowview/netscope-common-flowview.component";
import {NetscopeUtils} from "@app/netscope/netscope-utils";


echarts.use([ChordChart, AriaComponent, TitleComponent, TooltipComponent, CanvasRenderer, SVGRenderer]);

const EXPORTED_FILENAME = "netscope-networks";

function downloadFile(href: string, filename: string) {
  let link = document.createElement("a");
  link.setAttribute("href", href);
  link.setAttribute("download", filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Room left above the wheel for the title
const TITLE_HEIGHT = 40;
// Inner and outer radius of the wheel at zoom 1, in percent of half the smallest side of the chart
const BASE_RADIUS = [72, 78];
// Text sizes, in pixels
const TITLE_FONT_SIZE = 20;
const LABEL_FONT_SIZE = 15;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 20;
// A drag shorter than this, in pixels, is still a click
const DRAG_THRESHOLD = 3;


export class NetworksViewerRenderer implements FlowsRendererInterface {
    flowComponent: NetscopeCommonFlowviewComponent;

    chart: echarts.ECharts;
    resizeObserver: ResizeObserver;

    // The chord series cannot be zoomed nor moved by itself ('roam' is ignored): the wheel is zoomed by scaling
    // its radius, and moved by moving its center, in pixels (undefined: center of the chart)
    zoom: number = 1;
    centerX: number = undefined;
    centerY: number = undefined;

    dragStart: {x: number, y: number, centerX: number, centerY: number} = undefined;
    hasDragged: boolean = false;

    async render(flowComponent: NetscopeCommonFlowviewComponent): Promise<void> {
      this.flowComponent = flowComponent;

      let networkViewerData = await this.flowComponent.flowsRepository.getNetworkViewerData();

      this.flowComponent.datatableTabFlowsRepository = this.flowComponent.flowsRepository.clone();
      this.flowComponent.datatableTabFlowsRepository.showFirstPage();

      let ipToNetworkMap: Map<string, string> = new Map<string, string>();
      let networkFlowsMap: Map<string, NetworkLevelFlow> = new Map<string, NetworkLevelFlow>();

      // Init map of flows at networks level
      for (let link of networkViewerData) {
        if (! ipToNetworkMap.has(link.src_ipaddr)) {
          let subnetAddress = "unknown";
          if ((link.src_resource_type == "internet_ip")) {
            subnetAddress = "internet";
          } else {
            let addressParts = link.src_ipaddr.split(".");
            if (addressParts.length >= 4) {
              subnetAddress = `${addressParts[0]}.${addressParts[1]}.${addressParts[2]}.0/24`;
            }
          }
          ipToNetworkMap.set(link.src_ipaddr, subnetAddress);
        }
        if (! ipToNetworkMap.has(link.dst_ipaddr)) {
          let subnetAddress = "unknown";
          if ((link.dst_resource_type == "internet_ip")) {
            subnetAddress = "internet";
          } else {
            let addressParts = link.dst_ipaddr.split(".");
            if (addressParts.length >= 4) {
              subnetAddress = `${addressParts[0]}.${addressParts[1]}.${addressParts[2]}.0/24`;
            }
          }
          ipToNetworkMap.set(link.dst_ipaddr, subnetAddress);
        }

        let src_network = ipToNetworkMap.get(link.src_ipaddr);
        let dst_network = ipToNetworkMap.get(link.dst_ipaddr);

        if (src_network === dst_network) {
          continue;
        }

        let networkLevelKey = [src_network, dst_network].sort().join("__");
        if (!networkFlowsMap.has(networkLevelKey)) {
          let newNetworkLevelFlow = new NetworkLevelFlow();
          newNetworkLevelFlow.srcNetworkAddress = src_network;
          newNetworkLevelFlow.dstNetworkAddress = dst_network;
          newNetworkLevelFlow.flows = [];
          networkFlowsMap.set(networkLevelKey, newNetworkLevelFlow);
        }
        let networkLevelFlow = networkFlowsMap.get(networkLevelKey);
        networkLevelFlow.flows.push(link)
      }

      // Set the exchanged bytes at network level
      for (let networkLevelFlows of networkFlowsMap.values()) {
        let exchangedBytes = networkLevelFlows.flows.reduce((total, flow) => total + flow.exchanged_bytes, 0);
        networkLevelFlows.displayedValue = Math.log(exchangedBytes) ** 3;
        networkLevelFlows.exchangedBytes = exchangedBytes;
        networkLevelFlows.formattedExchangedBytes = NetscopeUtils.formatBytes(exchangedBytes);
      }

      // Networks are the arcs of the wheel, their volume being the one of all the flows they take part in
      let networkExchangedBytes: Map<string, number> = new Map<string, number>();
      for (let networkLevelFlow of networkFlowsMap.values()) {
        for (let network of [networkLevelFlow.srcNetworkAddress, networkLevelFlow.dstNetworkAddress]) {
          networkExchangedBytes.set(network, (networkExchangedBytes.get(network) ?? 0) + networkLevelFlow.exchangedBytes);
        }
      }

      let nodes = Array.from(networkExchangedBytes.entries())
        .map(([network, exchangedBytes]) => ({
          name: network,
          formattedExchangedBytes: NetscopeUtils.formatBytes(exchangedBytes)
        }));

      // Links between networks, shaded from the color of one network to the color of the other
      let links = Array.from(networkFlowsMap.values())
        .map((networkLevelFlow) => ({
          source: networkLevelFlow.srcNetworkAddress,
          target: networkLevelFlow.dstNetworkAddress,
          value: networkLevelFlow.displayedValue,
          formattedExchangedBytes: networkLevelFlow.formattedExchangedBytes
        }));

      let textColor = flowComponent.isDarkMode ? "#e8e8e8" : "#333333";

      let options: EChartsOption = {
        title: {
          text: 'Network level interactions',
          left: 'center',
          textStyle: {
            color: textColor,
            fontSize: TITLE_FONT_SIZE
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            if (params.dataType === 'edge') {
              return `<b>${params.data.source} &harr; ${params.data.target}</b><br>Volume: <b>${params.data.formattedExchangedBytes}</b>`;
            }
            return `<b>${params.name}</b><br>Volume: <b>${params.data.formattedExchangedBytes}</b>`;
          }
        },
        series: [{
          type: 'chord',
          data: nodes,
          links: links,
          top: TITLE_HEIGHT,
          radius: BASE_RADIUS.map((r) => `${r}%`),
          // Zooming and moving update the layout continuously, without waiting for animations
          animationDurationUpdate: 0,
          padAngle: 1,
          animation: false,
          cursor: 'pointer',
          lineStyle: {
            color: 'gradient',
            opacity: 0.5
          },
          label: {
            show: true,
            position: 'outside',
            color: textColor,
            fontSize: LABEL_FONT_SIZE
          },
          emphasis: {
            focus: 'adjacency',
            lineStyle: {
              opacity: 0.9
            }
          }
        }]
      };

      // Create the chart once its container is laid out
      setTimeout(() => {
        this.disposeChart();

        let container = flowComponent.divFlowsSvgEl.nativeElement;
        let e = document.createElement("div");
        e.id = "networks-viewer-chart";
        container.innerHTML = "";
        container.appendChild(e);

        // The container spans the whole zone of the view: the chart fills it, and follows its size (see resizeObserver)
        e.style.padding = "5px";
        e.style.boxSizing = "border-box";
        e.style.width = "100%";
        e.style.height = "100%";

        this.chart = echarts.init(e, ECHARTS_THEME);
        appendItemCountsToAriaLabel(this.chart);
        this.chart.setOption(options);
        this.applyView();
        this.listenToZoomAndMove();

        this.chart.on('click', (event: any) => {
          // The click ending a move does not select anything
          if (this.hasDragged) {
            return;
          }

          let whereClause = " where false ";
          let title = "Flows of selected network";
          if (event.dataType === 'edge') {
            let from = event.data.source;
            let to = event.data.target;
            title = `Flows between ${from} and ${to}`;

            let subClauses = ["false"];
            subClauses.push(` ((src_ipaddr::INET) <<= (INET '${from}') and (dst_ipaddr::INET) <<= (INET '${to}')) `);
            subClauses.push(` ((dst_ipaddr::INET) <<= (INET '${from}') and (src_ipaddr::INET) <<= (INET '${to}')) `);

            let combinedSubClauses = subClauses.join(" or ");
            whereClause = `where ${combinedSubClauses}`;

          } else if (event.name !== undefined) {
            title = `Flows of ${event.name}`;
            whereClause = `where (src_ipaddr::INET) <<= (INET '${event.name}') or (dst_ipaddr::INET) <<= (INET '${event.name}') `
          }

          // Fix Internet selection
          whereClause = whereClause.replaceAll("(src_ipaddr::INET) <<= (INET 'internet')", "src_resource_type = 'internet_ip'");
          whereClause = whereClause.replaceAll("(dst_ipaddr::INET) <<= (INET 'internet')", "dst_resource_type = 'internet_ip'");

          flowComponent.askShowSidePanel(title, whereClause);
          flowComponent.askRefreshAngular();
        });

        this.resizeObserver = new ResizeObserver(() => {
          this.chart?.resize();
          this.applyView();
        });
        this.resizeObserver.observe(container);
      }, 500);

      flowComponent.legend.hideLegend();
    }

    disposeChart = () => {
      this.resizeObserver?.disconnect();
      this.resizeObserver = undefined;
      this.chart?.dispose();
      this.chart = undefined;
    }

    // Center of the wheel, in pixels, as long as it has not been moved
    defaultCenter = (): [number, number] => {
      let height = this.chart.getHeight() - TITLE_HEIGHT;
      return [this.chart.getWidth() / 2, TITLE_HEIGHT + height / 2];
    }

    applyView = () => {
      if (!this.chart) {
        return;
      }
      let [defaultX, defaultY] = this.defaultCenter();
      let x = this.centerX ?? defaultX;
      let y = this.centerY ?? defaultY;

      this.chart.setOption({
        series: [{
          // The center is relative to the box of the series, which starts below the title
          center: [x, y - TITLE_HEIGHT],
          radius: BASE_RADIUS.map((r) => `${r * this.zoom}%`)
        }]
      }, {lazyUpdate: true});
    }

    // Zooms by the given factor, the point under (x, y) staying in place
    zoomAt = (x: number, y: number, factor: number) => {
      let newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.zoom * factor));
      let appliedFactor = newZoom / this.zoom;

      let [defaultX, defaultY] = this.defaultCenter();
      let currentX = this.centerX ?? defaultX;
      let currentY = this.centerY ?? defaultY;

      this.centerX = x + (currentX - x) * appliedFactor;
      this.centerY = y + (currentY - y) * appliedFactor;
      this.zoom = newZoom;

      this.applyView();
    }

    listenToZoomAndMove = () => {
      let zr = this.chart.getZr();

      zr.on('mousewheel', (e: any) => {
        e.event?.preventDefault();
        this.zoomAt(e.offsetX, e.offsetY, e.wheelDelta > 0 ? 1.1 : 1 / 1.1);
      });

      zr.on('mousedown', (e: any) => {
        let [defaultX, defaultY] = this.defaultCenter();
        this.dragStart = {
          x: e.offsetX,
          y: e.offsetY,
          centerX: this.centerX ?? defaultX,
          centerY: this.centerY ?? defaultY
        };
        this.hasDragged = false;
      });

      zr.on('mousemove', (e: any) => {
        if (this.dragStart == undefined) {
          return;
        }
        let dx = e.offsetX - this.dragStart.x;
        let dy = e.offsetY - this.dragStart.y;
        if (!this.hasDragged && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) {
          return;
        }
        this.hasDragged = true;
        this.centerX = this.dragStart.centerX + dx;
        this.centerY = this.dragStart.centerY + dy;
        this.applyView();
      });

      let endDrag = () => {
        this.dragStart = undefined;
      };
      zr.on('mouseup', endDrag);
      zr.on('globalout', endDrag);
    }

    zoomInVisualisation = (flowComponent: NetscopeCommonFlowviewComponent) => {
      if (this.chart) {
        this.zoomAt(this.chart.getWidth() / 2, this.chart.getHeight() / 2, 1.25);
      }
    }

    zoomOutVisualisation = (flowComponent: NetscopeCommonFlowviewComponent) => {
      if (this.chart) {
        this.zoomAt(this.chart.getWidth() / 2, this.chart.getHeight() / 2, 1 / 1.25);
      }
    }

    recenterVisualisation = (flowComponent: NetscopeCommonFlowviewComponent) => {
      this.zoom = 1;
      this.centerX = undefined;
      this.centerY = undefined;
      this.applyView();
    }

    /**
     * Writes the wheel as it is shown, zoomed and moved, to a file. The chart draws on a canvas, which only gives
     * back pictures: the SVG file is drawn by a hidden copy of the chart using the SVG renderer.
     */
    exportAs(format: string): void {
      if (!this.chart) {
        return;
      }

      // The chart is transparent: the file gets the background of the page, for the labels to stay readable
      let pageBackground = getComputedStyle(document.body).backgroundColor;
      let isTransparent = pageBackground == "" || pageBackground == "transparent" || pageBackground == "rgba(0, 0, 0, 0)";
      let backgroundColor = isTransparent ? (this.flowComponent.isDarkMode ? "#1b2a32" : "#ffffff") : pageBackground;

      if (format == "png") {
        let dataUrl = this.chart.getDataURL({type: "png", pixelRatio: 2, backgroundColor: backgroundColor});
        downloadFile(dataUrl, `${EXPORTED_FILENAME}.png`);
        return;
      }

      if (format == "svg") {
        let hiddenContainer = document.createElement("div");
        let svgChart = echarts.init(hiddenContainer, null, {
          renderer: "svg",
          width: this.chart.getWidth(),
          height: this.chart.getHeight()
        });
        try {
          svgChart.setOption({
            ...this.chart.getOption(),
            animation: false,
            backgroundColor: backgroundColor
          });
          let blob = new Blob([svgChart.renderToSVGString()], {type: "image/svg+xml;charset=utf-8"});
          let url = URL.createObjectURL(blob);
          downloadFile(url, `${EXPORTED_FILENAME}.svg`);
          URL.revokeObjectURL(url);
        } finally {
          svgChart.dispose();
        }
        return;
      }

      console.warn(`[networks-viewer] cannot export as '${format}'`);
    }

    destroy(flowComponent: NetscopeCommonFlowviewComponent): void {
      this.disposeChart();
    }

    update(flowComponent: NetscopeCommonFlowviewComponent): void {}

    isOutdated(): boolean {
      return this.flowComponent.selectedView !== "networks-viewer";
    }
}
